/**
 * Revenue Calculation — the page's regions, in render order.
 *
 * Pure data. The shell renders this list; it does not hard-code a fixed number of blocks,
 * which is why adding the assumptions panel (card 3065) is one entry here rather than a
 * fifth branch in the page.
 *
 * ⛔ EXTRACTED FROM `RevenueCalculationPage.jsx`, WHICH ARRIVED AT 317 LINES — already over
 *   the 300-line standard before this card touched it. Card 3065's own criterion caps that
 *   file at ~320, and a fifth region cannot be added inside that budget. The page RE-EXPORTS
 *   `REGIONS`, so any existing importer keeps working; do not "tidy" that re-export away.
 *
 * 🔑 EVERY `emptyNote` SAYS WHAT AN EMPTY PANEL MEANS. A region that renders nothing is
 *   indistinguishable from one that is legitimately empty unless it says so, and on a money
 *   page the difference between "the check found nothing" and "the check did not run" is the
 *   whole point.
 */

export const REGIONS = [
  {
    id: 'kpi-row',
    label: 'Key figures',
    note: 'Gross revenue, net settled revenue, variable expense, contribution profit and margin, static expense, fully-loaded profit and margin, and missing-cost exposure.',
    emptyTitle: 'No key figures yet',
    emptyNote: 'No orders fall in the selected date range, so there is nothing to total. A blocked input reads as missing here, never as zero.',
    skeletonRows: 3
  },
  {
    id: 'product-table',
    label: 'Product economics',
    note: 'One row per product, expandable to dosage and supply term, with the expense lines and the data quality of each.',
    emptyTitle: 'No product rows yet',
    emptyNote: 'No product matches the current filters. Products with an incomplete cost basis are listed too — shown as missing, never dropped.',
    skeletonRows: 4
  },
  {
    id: 'blockers-panel',
    label: 'Missing and conflicting inputs',
    note: 'The money blockers that are still unanswered, each with what is missing, which page figures it holds up, who owns the answer, and the document it came from. Select one to filter the product table to the rows it blocks.',
    emptyTitle: 'No blockers loaded yet',
    emptyNote: 'The blockers endpoint returned nothing. Every unanswered input is rendered here as a blocker rather than being guessed at, so an empty panel means the endpoint failed — not that the questions are answered.',
    skeletonRows: 3
  },
  {
    id: 'assumptions-panel',
    label: 'Ruled assumptions and provenance',
    note: 'The cost lines X authorized on 2026-08-28, each with its effective date, its authorizer and what it supersedes. Every one is a dated ASSUMPTION — not a vendor invoice, not a vendor quote and not a verified rate — and each is labelled as such on the line itself.',
    emptyTitle: 'No assumptions loaded yet',
    emptyNote: 'The assumptions endpoint returned nothing. These figures are the basis of every expense line on this page, so an empty panel does NOT mean there are no assumptions — it means the page cannot currently show you which ones it is using.',
    skeletonRows: 3
  },
  {
    id: 'monthly-closeout',
    label: 'Monthly closeout — January to August 2026',
    note: 'One row per UTC calendar month: orders, gross on the COLLECTED basis, the failed-payment money excluded from it, the count of lines whose cost is still unresolved, expense, profit, and the source quality behind them. Every month is read from this page’s own summary endpoint over its own window, so a month here and the same window in the filters above are the same query.',
    emptyTitle: 'No monthly closeout yet',
    emptyNote: 'The eight month windows have not returned. This panel is a closeout, so an empty panel does NOT mean the months were empty — it means the figures could not be read, and no month here should be taken as zero.',
    skeletonRows: 4
  },
  {
    id: 'reconciliation-panel',
    label: 'Reconciliation',
    note: 'Whether the four surfaces that total these orders agree to the cent — this page, the stored order cost columns, order detail, and the daily/weekly/monthly report arithmetic. Each row carries its own population count and its own predicate.',
    emptyTitle: 'Nothing to reconcile yet',
    emptyNote: 'The reconciliation endpoint returned nothing. This panel is the page’s own self-test, so an empty panel does not mean the surfaces agree — it means the check did not run.',
    skeletonRows: 2
  }
];
